import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import Icon from '../../../icons/Icon';
import EditableFieldRow from '../../custom/EditableFieldRow';
import ExpandableGroup, { ExpandableGroupHeaderContext, useAccordionState } from '../../custom/ExpandableGroup';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarAccordionSectionHeader from './SarAccordionSectionHeader';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { SarHesapSahibi, SarValidationResult } from '../SarInterfaces';
import { SarXmlTags } from '../../../constant/SarTagConstants';
import SarParser from '../SarParser';
import HESAP_TAGS = SarXmlTags.HESAP_TAGS;
import HESAP_OWNER_TAGS = SarXmlTags.HESAP_OWNER_TAGS;

const HesaplarAccordion = ({ xmlContent, mutateXmlContent, validate }: Props) => {
  const invalidFieldIds = useMemo<Set<string>>(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    return SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
  }, [validate]);
  const hesaplar = useMemo(() => SarParser.parseHesaplar(xmlContent), [xmlContent]);
  const accordion = useAccordionState();

  const errorsArray = validate?.errors ?? [];
  const errorsKey = errorsArray.map((e) => `${e.lineStart}-${e.lineEnd}-${e.message}`).join('|');
  const hesapErrors = useMemo<Map<number, boolean>>(() => {
    const errors = new Map<number, boolean>();
    const currentInvalidFieldIds = errorsArray.length > 0 ? SarValidationCategoryMapper.getInvalidFieldIds(errorsArray) : new Set<string>();

    hesaplar.forEach((_, index) => {
      const hesapPrefix = `hesap-${index}-`;
      const hasError = Array.from(currentInvalidFieldIds).some((fieldId) => {
        return fieldId.startsWith(hesapPrefix);
      });
      errors.set(index, hasError);
    });
    return errors;
  }, [hesaplar.length, errorsKey]);
  const createGroupHeaderRenderer =
    (fieldPath: string) =>
    ({ label, hasError }: ExpandableGroupHeaderContext) => <SarAccordionSectionHeader label={label} fieldPath={fieldPath} hasError={hasError} />;

  const buildInfoConfig = (path: string, type: string = 'Metin') => ({
    path,
    type,
    mode: 'bubble' as const,
  });

  const handleFieldChange = useCallback(
    (hesapIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const hesap = document.getElementsByTagName('hesap').item(hesapIndex);
        if (!hesap) return;
        let target = hesap.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          hesap.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleOwnerFieldChange = useCallback(
    (hesapIndex: number, ownerIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const hesap = document.getElementsByTagName('hesap').item(hesapIndex);
        if (!hesap) return;
        let ownersParent = hesap.getElementsByTagName('hesapsahipleri').item(0);
        if (!ownersParent) {
          ownersParent = document.createElement('hesapsahipleri');
          hesap.appendChild(ownersParent);
        }
        const ownerNode = ownersParent.getElementsByTagName('hesapsahibi').item(ownerIndex);
        if (!ownerNode) return;
        let target = ownerNode.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          ownerNode.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddHesap = () => {
    mutateXmlContent((document) => {
      let parent = document.getElementsByTagName('hesaplar').item(0);
      if (!parent) {
        parent = document.createElement('hesaplar');
        document.documentElement.appendChild(parent);
      }
      const hesap = document.createElement('hesap');
      HESAP_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        hesap.appendChild(element);
      });
      hesap.appendChild(document.createElement('hesapsahipleri'));
      parent.appendChild(hesap);
    });
  };

  const handleAddOwner = (hesapIndex: number) => {
    mutateXmlContent((document) => {
      const hesap = document.getElementsByTagName('hesap').item(hesapIndex);
      if (!hesap) return;
      let ownersParent = hesap.getElementsByTagName('hesapsahipleri').item(0);
      if (!ownersParent) {
        ownersParent = document.createElement('hesapsahipleri');
        hesap.appendChild(ownersParent);
      }
      const ownerNode = document.createElement('hesapsahibi');
      HESAP_OWNER_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        ownerNode.appendChild(element);
      });
      ownersParent.appendChild(ownerNode);
    });
  };

  return (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'flex items-center justify-between'}>
        <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
        <button
          onClick={handleAddHesap}
          type='button'
          className='w-5 h-5 hover:bg-f-gray-2 rounded-sm flex items-center justify-center transition-all duration-200'
        >
          <Icon icon='add' className='text-[#1D2939]' />
        </button>
      </div>
      {hesaplar.length === 0 ? (
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz hesap bulunmuyor.
        </div>
      ) : (
        <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
          {hesaplar.map((hesap, index) => {
            const hesapKey = `hesap-${index}`;
            const expanded = accordion.isExpanded(hesapKey);
            const hasHesapError = hesapErrors.get(index) ?? false;
            return (
              <div key={hesap.id} className={'bg-white rounded-t-xl rounded-b-xl'}>
                <div
                  role={'button'}
                  tabIndex={0}
                  className={`flex w-full rounded-t-xl items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left border-b ${index === hesaplar.length - 1 ? '' : 'border-b'}`}
                  onClick={() => accordion.toggle(hesapKey)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      accordion.toggle(hesapKey);
                    }
                  }}
                >
                  <div className='flex items-center gap-x-3'>
                    <div>
                      <AccordionOpenCloseIcon expanded={expanded} hasError={hasHesapError} />
                    </div>
                    <SarAccordionSectionHeader label={`Hesap ${index + 1}`} fieldPath={'hesap'} hasError={hasHesapError} />
                  </div>
                  <AccordionIndicator expanded={expanded} />
                </div>
                {expanded && (
                  <div className={'flex flex-col divide-y divide-[#F2F4F7] border-b'}>
                    <EditableFieldRow
                      label={'Şahıs / Firma Hesabı'}
                      value={hesap.personOrCompanyHesap}
                      onChange={(value) => handleFieldChange(index, 'sahisfirmasihesabi', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-sahisfirmasihesabi`)}
                      infoConfig={buildInfoConfig('hesap.sahisFirmasiHesabi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Hesap Türü'}
                      value={hesap.hesapType}
                      onChange={(value) => handleFieldChange(index, 'hesapturu', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-hesapturu`)}
                      infoConfig={buildInfoConfig('hesap.hesapTuru', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Hesap No'}
                      value={hesap.hesapNumber}
                      onChange={(value) => handleFieldChange(index, 'hesapno', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-hesapno`)}
                      infoConfig={buildInfoConfig('hesap.hesapNo')}
                    />
                    <EditableFieldRow
                      label={'IBAN'}
                      value={hesap.iban}
                      onChange={(value) => handleFieldChange(index, 'iban', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-iban`)}
                      infoConfig={buildInfoConfig('hesap.iban')}
                    />
                    <EditableFieldRow
                      label={'Açılış Tarihi'}
                      value={hesap.openingDate}
                      onChange={(value) => handleFieldChange(index, 'acilistarihi', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-acilistarihi`)}
                      infoConfig={buildInfoConfig('hesap.acilisTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'Kapanış Tarihi'}
                      value={hesap.closingDate}
                      onChange={(value) => handleFieldChange(index, 'kapanistarihi', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-kapanistarihi`)}
                      infoConfig={buildInfoConfig('hesap.kapanisTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'Son İşlem Tarihi'}
                      value={hesap.lastTransactionDate}
                      onChange={(value) => handleFieldChange(index, 'sonislemtarihi', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-sonislemtarihi`)}
                      infoConfig={buildInfoConfig('hesap.sonIslemTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'Bakiye Tipi'}
                      value={hesap.balanceType}
                      onChange={(value) => handleFieldChange(index, 'bakiyetp', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-bakiyetp`)}
                      infoConfig={buildInfoConfig('hesap.bakiyeTP')}
                    />
                    <EditableFieldRow
                      label={'Bakiye'}
                      value={hesap.balance}
                      onChange={(value) => handleFieldChange(index, 'bakiye', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-bakiye`)}
                      infoConfig={buildInfoConfig('hesap.bakiye')}
                    />
                    <EditableFieldRow
                      label={'Para Birimi'}
                      value={hesap.currency}
                      onChange={(value) => handleFieldChange(index, 'parabirimi', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-parabirimi`)}
                      infoConfig={buildInfoConfig('hesap.paraBirimi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Kısıtlama Türü'}
                      value={hesap.restrictionType}
                      onChange={(value) => handleFieldChange(index, 'kisitlamaturu', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-kisitlamaturu`)}
                      infoConfig={buildInfoConfig('hesap.kisitlamaturu')}
                    />
                    <EditableFieldRow
                      label={'Kısıtlama Diğer Açıklama'}
                      value={hesap.restrictionDescription}
                      multiline
                      onChange={(value) => handleFieldChange(index, 'kisitlamadigeraciklama', value)}
                      hasError={invalidFieldIds.has(`hesap-${index}-kisitlamadigeraciklama`)}
                      infoConfig={buildInfoConfig('hesap.kisitlamadigeraciklama')}
                    />
                    <ExpandableGroup<SarHesapSahibi>
                      baseKey={`${hesapKey}-owners`}
                      label={'Hesap Sahipleri'}
                      items={hesap.owners}
                      accordion={accordion}
                      getItemTitle={(_, ownerIndex) => `Hesap Sahibi ${ownerIndex + 1}`}
                      onAdd={() => handleAddOwner(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('hesapSahipleri')}
                      hasError={
                        invalidFieldIds.has(`hesap-${index}-hesapsahibi`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`hesap-${index}-hesapsahibi`))
                      }
                      getItemHasError={(_, ownerIndex) => {
                        return (
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-magdur`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-adsoyadunvan`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-tckn`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-vkn`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-ykn`) ||
                          invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-pasaport`)
                        );
                      }}
                      renderItems={(owner, ownerIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl'}>
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Mağdur'}  
                            value={owner.victimFlag}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'magdur', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-magdur`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.magdur', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Ad Soyad / Unvan'}
                            value={owner.fullNameOrTitle}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'adsoyadunvan', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-adsoyadunvan`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.adSoyadUnvan')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'TCKN'}
                            value={owner.nationalId}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'tckn', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-tckn`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.tckn', 'Kimlik')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'VKN'}
                            value={owner.taxId}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'vkn', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-vkn`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.vkn', 'Kimlik')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'YKN'}
                            value={owner.foreignId}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'ykn', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-ykn`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.ykn', 'Kimlik')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Pasaport'}
                            value={owner.passport}
                            onChange={(value) => handleOwnerFieldChange(index, ownerIndex, 'pasaport', value)}
                            hasError={invalidFieldIds.has(`hesap-${index}-hesapsahibi-${ownerIndex}-pasaport`)}
                            infoConfig={buildInfoConfig('hesapSahipleri.hesapSahibi.pasaport')}
                          />
                        </div>
                      )}
                      emptyLabel={'Hesap sahibi bulunamadı.'}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface Props {
  xmlContent?: string;
  mutateXmlContent: (mutator: (document: Document) => void) => void;
  validate?: SarValidationResult;
}

export default HesaplarAccordion;
